import { Injectable } from '@angular/core';

import { of } from 'rxjs';
import { map, mergeMap, concatMap, catchError, withLatestFrom } from 'rxjs/operators';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { RouterReducerState } from '@ngrx/router-store';
import { Store, select } from '@ngrx/store';

import { RouterStateUrl, ReferenceService } from '@sd-portal/shared/util';

import { State } from '..';
import * as actions from './posted-flight.actions';
import { PostedFlightAuditService } from '../../posted-flight-audit.service';

@Injectable()
export class PostedFlightEffects {
  constructor(
    private readonly postedFlightAuditService: PostedFlightAuditService,
    private readonly referenceService: ReferenceService,
    private readonly actions$: Actions,
    private readonly store: Store<{
      router: RouterReducerState<RouterStateUrl>;
      sharedAudit: State;
    }>
  ) {}

  loadApproachTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadApproachTypes),
      mergeMap(() =>
        this.referenceService.getReferenceItemsNoCache('ApproachType', true).pipe(
          map(payload => actions.ApproachTypesLoaded({ payload })),
          catchError(err => of(actions.ApproachTypesLoadError(err)))
        )
      )
    )
  );

  loadExpenseCategoryTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadExpenseCategoryTypes),
      mergeMap(() =>
        this.referenceService.getReferenceItemsNoCache('ExpenseCategory', true).pipe(
          map(payload => actions.ExpenseCategoryTypesLoaded({ payload })),
          catchError(err => of(actions.ExpenseCategoryTypesLoadError(err)))
        )
      )
    )
  );

  loadPostedFlightSkeleton$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadPostedFlightSkeleton),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([_action, state]) => {
        const postedFlightId = +state.router.state.params['id'];

        return this.postedFlightAuditService.getPostedFlightSkeleton(postedFlightId).pipe(
          map(payload => actions.PostedFlightSkeletonLoaded({ payload })),
          catchError(err => of(actions.PostedFlightSkeletonLoaded(err)))
        );
      })
    )
  );

  loadPostedFlightAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadPostedFlightAudit),
      mergeMap(action =>
        this.postedFlightAuditService.getPostedFlightAudit(action.postedFlightId).pipe(
          map(audits =>
            actions.PostedFlightAuditLoaded({ postedFlightId: action.postedFlightId, audits })
          ),
          catchError(err => of(actions.PostedFlightAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPagePostedFlightAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPagePostedFlightAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.postedFlight.postedFlightAudit.lastAuditLogId;
        }
        return this.postedFlightAuditService
          .getPostedFlightAudit(action.postedFlightId, nextPageNumber, undefined, lastAuditLogId)
          .pipe(
            map(audits =>
              actions.PostedFlightAuditLoaded({ postedFlightId: action.postedFlightId, audits })
            ),
            catchError(err => of(actions.PostedFlightAuditLoadError(err)))
          );
      })
    )
  );

  loadFlightCrewMemberAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadFlightCrewMemberAudit),
      mergeMap(action =>
        this.postedFlightAuditService.getFlightCrewMemberAudit(action.flightCrewMemberId).pipe(
          map(audits =>
            actions.FlightCrewMemberAuditLoaded({
              flightCrewMemberId: action.flightCrewMemberId,
              audits
            })
          ),
          catchError(err => of(actions.FlightCrewMemberAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageFlightCrewMemberAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageFlightCrewMemberAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.postedFlight.flightCrewMemberAudit.lastAuditLogId;
        }
        return this.postedFlightAuditService
          .getFlightCrewMemberAudit(
            action.flightCrewMemberId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.FlightCrewMemberAuditLoaded({
                flightCrewMemberId: action.flightCrewMemberId,
                audits
              })
            ),
            catchError(err => of(actions.FlightCrewMemberAuditLoadError(err)))
          );
      })
    )
  );

  loadFlightBusinessPersonAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadFlightBusinessPersonAudit),
      mergeMap(action =>
        this.postedFlightAuditService
          .getFlightBusinessPersonAudit(action.flightBusinessPersonId)
          .pipe(
            map(audits =>
              actions.FlightBusinessPersonAuditLoaded({
                flightBusinessPersonId: action.flightBusinessPersonId,
                audits
              })
            ),
            catchError(err => of(actions.FlightBusinessPersonAuditLoadError(err)))
          )
      )
    )
  );

  loadNextPageFlightBusinessPersonAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageFlightBusinessPersonAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.postedFlight.flightBusinessPersonAudit.lastAuditLogId;
        }
        return this.postedFlightAuditService
          .getFlightBusinessPersonAudit(
            action.flightBusinessPersonId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.FlightBusinessPersonAuditLoaded({
                flightBusinessPersonId: action.flightBusinessPersonId,
                audits
              })
            ),
            catchError(err => of(actions.FlightBusinessPersonAuditLoadError(err)))
          );
      })
    )
  );

  loadLogbookApproachAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadLogbookApproachAudit),
      mergeMap(action =>
        this.postedFlightAuditService.getLogbookApproachAudit(action.logbookApproachId).pipe(
          map(audits =>
            actions.LogbookApproachAuditLoaded({
              logbookApproachId: action.logbookApproachId,
              audits
            })
          ),
          catchError(err => of(actions.LogbookApproachAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageLogbookApproachAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageLogbookApproachAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.postedFlight.logbookApproachAudit.lastAuditLogId;
        }
        return this.postedFlightAuditService
          .getLogbookApproachAudit(
            action.logbookApproachId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.LogbookApproachAuditLoaded({
                logbookApproachId: action.logbookApproachId,
                audits
              })
            ),
            catchError(err => of(actions.LogbookApproachAuditLoadError(err)))
          );
      })
    )
  );

  loadFlightPassengerAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadFlightPassengerAudit),
      mergeMap(action =>
        this.postedFlightAuditService.getFlightPassengerAudit(action.flightPassengerId).pipe(
          map(audits =>
            actions.FlightPassengerAuditLoaded({
              flightPassengerId: action.flightPassengerId,
              audits
            })
          ),
          catchError(err => of(actions.FlightPassengerAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageFlightPassengerAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageFlightPassengerAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.postedFlight.flightPassengerAudit.lastAuditLogId;
        }
        return this.postedFlightAuditService
          .getFlightPassengerAudit(
            action.flightPassengerId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.FlightPassengerAuditLoaded({
                flightPassengerId: action.flightPassengerId,
                audits
              })
            ),
            catchError(err => of(actions.FlightPassengerAuditLoadError(err)))
          );
      })
    )
  );

  loadExpenseAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadExpenseAudit),
      mergeMap(action =>
        this.postedFlightAuditService.getExpenseAudit(action.expenseId).pipe(
          map(audits => actions.ExpenseAuditLoaded({ expenseId: action.expenseId, audits })),
          catchError(err => of(actions.ExpenseAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageExpenseAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageExpenseAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.postedFlight.expenseAudit.lastAuditLogId;
        }
        return this.postedFlightAuditService
          .getExpenseAudit(action.expenseId, nextPageNumber, undefined, lastAuditLogId)
          .pipe(
            map(audits => actions.ExpenseAuditLoaded({ expenseId: action.expenseId, audits })),
            catchError(err => of(actions.ExpenseAuditLoadError(err)))
          );
      })
    )
  );

  loadPostedFlightEngineAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadPostedFlightEngineAudit),
      mergeMap(action =>
        this.postedFlightAuditService.getPostedFlightEngineAudit(action.postedFlightEngineId).pipe(
          map(audits =>
            actions.PostedFlightEngineAuditLoaded({
              postedFlightEngineId: action.postedFlightEngineId,
              audits
            })
          ),
          catchError(err => of(actions.PostedFlightEngineAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPagePostedFlightEngineAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPagePostedFlightEngineAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.postedFlight.postedFlightEngineAudit.lastAuditLogId;
        }
        return this.postedFlightAuditService
          .getPostedFlightEngineAudit(
            action.postedFlightEngineId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.PostedFlightEngineAuditLoaded({
                postedFlightEngineId: action.postedFlightEngineId,
                audits
              })
            ),
            catchError(err => of(actions.PostedFlightEngineAuditLoadError(err)))
          );
      })
    )
  );

  loadPostedFlightComponentAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadPostedFlightComponentAudit),
      mergeMap(action =>
        this.postedFlightAuditService
          .getPostedFlightComponentAudit(action.postedFlightComponentId)
          .pipe(
            map(audits =>
              actions.PostedFlightComponentAuditLoaded({
                postedFlightComponentId: action.postedFlightComponentId,
                audits
              })
            ),
            catchError(err => of(actions.PostedFlightComponentAuditLoadError(err)))
          )
      )
    )
  );

  loadNextPagePostedFlightComponentAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPagePostedFlightComponentAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.postedFlight.postedFlightComponentAudit.lastAuditLogId;
        }
        return this.postedFlightAuditService
          .getPostedFlightComponentAudit(
            action.postedFlightComponentId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.PostedFlightComponentAuditLoaded({
                postedFlightComponentId: action.postedFlightComponentId,
                audits
              })
            ),
            catchError(err => of(actions.PostedFlightComponentAuditLoadError(err)))
          );
      })
    )
  );

  loadPostedFlightFuelAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadPostedFlightFuelAudit),
      mergeMap(action =>
        this.postedFlightAuditService.getPostedFlightFuelAudit(action.postedFlightId).pipe(
          map(audits =>
            actions.PostedFlightFuelAuditLoaded({ postedFlightId: action.postedFlightId, audits })
          ),
          catchError(err => of(actions.PostedFlightFuelAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPagePostedFlightFuelAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPagePostedFlightFuelAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.postedFlight.postedFlightFuelAudit.lastAuditLogId;
        }
        return this.postedFlightAuditService
          .getPostedFlightFuelAudit(
            action.postedFlightId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.PostedFlightFuelAuditLoaded({ postedFlightId: action.postedFlightId, audits })
            ),
            catchError(err => of(actions.PostedFlightFuelAuditLoadError(err)))
          );
      })
    )
  );

  loadPostedFlightAdditionalDetailAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadPostedFlightAdditionalDetailAudit),
      mergeMap(action =>
        this.postedFlightAuditService
          .getPostedFlightAdditionalDetailAudit(action.postedFlightId)
          .pipe(
            map(audits =>
              actions.PostedFlightAdditionalDetailAuditLoaded({
                postedFlightId: action.postedFlightId,
                audits
              })
            ),
            catchError(err => of(actions.PostedFlightAdditionalDetailAuditLoadError(err)))
          )
      )
    )
  );

  loadNextPagePostedFlightAdditionalDetailAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPagePostedFlightAdditionalDetailAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId =
            state.sharedAudit.postedFlight.postedFlightAdditionalDetailAudit.lastAuditLogId;
        }
        return this.postedFlightAuditService
          .getPostedFlightAdditionalDetailAudit(
            action.postedFlightId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.PostedFlightAdditionalDetailAuditLoaded({
                postedFlightId: action.postedFlightId,
                audits
              })
            ),
            catchError(err => of(actions.PostedFlightAdditionalDetailAuditLoadError(err)))
          );
      })
    )
  );

  loadOilFluidAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadOilFluidAudit),
      mergeMap(action =>
        this.postedFlightAuditService.getOilFluidAudit(action.postedFlightId).pipe(
          map(audits =>
            actions.OilFluidAuditLoaded({ postedFlightId: action.postedFlightId, audits })
          ),
          catchError(err => of(actions.OilFluidAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageOilFluidAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageOilFluidAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.postedFlight.oilFluidAudit.lastAuditLogId;
        }
        return this.postedFlightAuditService
          .getOilFluidAudit(action.postedFlightId, nextPageNumber, undefined, lastAuditLogId)
          .pipe(
            map(audits =>
              actions.OilFluidAuditLoaded({ postedFlightId: action.postedFlightId, audits })
            ),
            catchError(err => of(actions.OilFluidAuditLoadError(err)))
          );
      })
    )
  );

  loadEngineOilFluidAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadEngineOilFluidAudit),
      mergeMap(action =>
        this.postedFlightAuditService.getEngineOilFluidAudit(action.postedFlightEngineId).pipe(
          map(audits =>
            actions.EngineOilFluidAuditLoaded({
              postedFlightEngineId: action.postedFlightEngineId,
              audits
            })
          ),
          catchError(err => of(actions.EngineOilFluidAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageEngineOilFluidAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageEngineOilFluidAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.postedFlight.engineOilFluidAudit.lastAuditLogId;
        }
        return this.postedFlightAuditService
          .getEngineOilFluidAudit(
            action.postedFlightEngineId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.EngineOilFluidAuditLoaded({
                postedFlightEngineId: action.postedFlightEngineId,
                audits
              })
            ),
            catchError(err => of(actions.EngineOilFluidAuditLoadError(err)))
          );
      })
    )
  );

  loadComponentOilFluidAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadComponentOilFluidAudit),
      mergeMap(action =>
        this.postedFlightAuditService
          .getComponentOilFluidAudit(action.postedFlightComponentId)
          .pipe(
            map(audits =>
              actions.ComponentOilFluidAuditLoaded({
                postedFlightComponentId: action.postedFlightComponentId,
                audits
              })
            ),
            catchError(err => of(actions.ComponentOilFluidAuditLoadError(err)))
          )
      )
    )
  );

  loadNextPageComponentOilFluidAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageComponentOilFluidAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.postedFlight.componentOilFluidAudit.lastAuditLogId;
        }
        return this.postedFlightAuditService
          .getComponentOilFluidAudit(
            action.postedFlightComponentId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.ComponentOilFluidAuditLoaded({
                postedFlightComponentId: action.postedFlightComponentId,
                audits
              })
            ),
            catchError(err => of(actions.ComponentOilFluidAuditLoadError(err)))
          );
      })
    )
  );

  loadPostedFlightDeAntiIceAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadPostedFlightDeAntiIceAudit),
      mergeMap(action =>
        this.postedFlightAuditService.getPostedFlightDeAntiIceAudit(action.postedFlightId).pipe(
          map(audits =>
            actions.PostedFlightDeAntiIceAuditLoaded({
              postedFlightId: action.postedFlightId,
              audits
            })
          ),
          catchError(err => of(actions.PostedFlightDeAntiIceAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPagePostedFlightDeAntiIceAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPagePostedFlightDeAntiIceAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.postedFlight.postedFlightDeAntiIceAudit.lastAuditLogId;
        }
        return this.postedFlightAuditService
          .getPostedFlightDeAntiIceAudit(
            action.postedFlightId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.PostedFlightDeAntiIceAuditLoaded({
                postedFlightId: action.postedFlightId,
                audits
              })
            ),
            catchError(err => of(actions.PostedFlightDeAntiIceAuditLoadError(err)))
          );
      })
    )
  );

  loadPostedFlightNotesAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadPostedFlightNotesAudit),
      mergeMap(action =>
        this.postedFlightAuditService.getPostedFlightNotesAudit(action.postedFlightId).pipe(
          map(audits =>
            actions.PostedFlightNotesAuditLoaded({ postedFlightId: action.postedFlightId, audits })
          ),
          catchError(err => of(actions.PostedFlightNotesAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPagePostedFlightNotesAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPagePostedFlightNotesAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.postedFlight.postedFlightNotesAudit.lastAuditLogId;
        }
        return this.postedFlightAuditService
          .getPostedFlightNotesAudit(
            action.postedFlightId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.PostedFlightNotesAuditLoaded({
                postedFlightId: action.postedFlightId,
                audits
              })
            ),
            catchError(err => of(actions.PostedFlightNotesAuditLoadError(err)))
          );
      })
    )
  );

  loadFuelUpliftAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadFuelUpliftAudit),
      mergeMap(action =>
        this.postedFlightAuditService.getFuelUpliftAudit(action.postedFlightId).pipe(
          map(audits =>
            actions.FuelUpliftAuditLoaded({ postedFlightId: action.postedFlightId, audits })
          ),
          catchError(err => of(actions.FuelUpliftAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageFuelUpliftAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageFuelUpliftAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.postedFlight.fuelUpliftAudit.lastAuditLogId;
        }
        return this.postedFlightAuditService
          .getFuelUpliftAudit(action.postedFlightId, nextPageNumber, undefined, lastAuditLogId)
          .pipe(
            map(audits =>
              actions.FuelUpliftAuditLoaded({ postedFlightId: action.postedFlightId, audits })
            ),
            catchError(err => of(actions.FuelUpliftAuditLoadError(err)))
          );
      })
    )
  );

  loadBusinessCategoryAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadBusinessCategoryAudit),
      mergeMap(action =>
        this.postedFlightAuditService.getBusinessCategoryAudits(action.flightId).pipe(
          map(audits => actions.BusinessCategoryAuditLoaded({ flightId: action.flightId, audits })),
          catchError(err => of(actions.BusinessCategoryAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageBusinessCategoryAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageBusinessCategoryAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.postedFlight.businessCategoryAudit.lastAuditLogId;
        }
        return this.postedFlightAuditService
          .getBusinessCategoryAudits(action.flightId, nextPageNumber, undefined, lastAuditLogId)
          .pipe(
            map(audits =>
              actions.BusinessCategoryAuditLoaded({ flightId: action.flightId, audits })
            ),
            catchError(err => of(actions.BusinessCategoryAuditLoadError(err)))
          );
      })
    )
  );

  loadDepartmentAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadDepartmentAudit),
      mergeMap(action =>
        this.postedFlightAuditService.getDepartmentAudits(action.flightId).pipe(
          map(audits => actions.DepartmentAuditLoaded({ flightId: action.flightId, audits })),
          catchError(err => of(actions.DepartmentAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageDepartmentAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageDepartmentAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.postedFlight.departmentAudit.lastAuditLogId;
        }
        return this.postedFlightAuditService
          .getDepartmentAudits(action.flightId, nextPageNumber, undefined, lastAuditLogId)
          .pipe(
            map(audits => actions.DepartmentAuditLoaded({ flightId: action.flightId, audits })),
            catchError(err => of(actions.DepartmentAuditLoadError(err)))
          );
      })
    )
  );

  loadLogbookAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadLogbookAudit),
      mergeMap(action =>
        this.postedFlightAuditService.getLogbookAudits(action.logbookId).pipe(
          map(audits => actions.LogbookAuditLoaded({ logbookId: action.logbookId, audits })),
          catchError(err => of(actions.LogbookAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageLogbookAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageLogbookAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.postedFlight.logbookAudit.lastAuditLogId;
        }
        return this.postedFlightAuditService
          .getLogbookAudits(action.logbookId, nextPageNumber, undefined, lastAuditLogId)
          .pipe(
            map(audits => actions.LogbookAuditLoaded({ logbookId: action.logbookId, audits })),
            catchError(err => of(actions.LogbookAuditLoadError(err)))
          );
      })
    )
  );

  loadCrewMemberDutyAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadCrewMemberDutyAudit),
      mergeMap(action =>
        this.postedFlightAuditService.getCrewMemberDutyAudits(action.crewMemberDutyId).pipe(
          map(audits =>
            actions.CrewMemberDutyAuditLoaded({ crewMemberDutyId: action.crewMemberDutyId, audits })
          ),
          catchError(err => of(actions.CrewMemberDutyAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageCrewMemberDutyAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageCrewMemberDutyAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.postedFlight.crewMemberDutyAudit.lastAuditLogId;
        }
        return this.postedFlightAuditService
          .getCrewMemberDutyAudits(
            action.crewMemberDutyId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.CrewMemberDutyAuditLoaded({
                crewMemberDutyId: action.crewMemberDutyId,
                audits
              })
            ),
            catchError(err => of(actions.CrewMemberDutyAuditLoadError(err)))
          );
      })
    )
  );

  //AirFrame/Engine cycles
  loadAirFrameLandingsAuditEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadAirFrameLandingsAuditAction),
      mergeMap(action =>
        this.postedFlightAuditService.getAirFrameLandingsAudit(action.postedFlightId).pipe(
          map(audits =>
            actions.AirFrameLandingsAuditLoadedAction({
              postedFlightId: action.postedFlightId,
              audits
            })
          ),
          catchError(err => of(actions.AirFrameLandingsAuditLoadErrorAction(err)))
        )
      )
    )
  );

  loadNextPageAirFrameLandingsAuditEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageAirFrameLandingsAuditAction),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.postedFlight.airframeLandingsAudit.lastAuditLogId;
        }
        return this.postedFlightAuditService
          .getAirFrameLandingsAudit(
            action.postedFlightId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.AirFrameLandingsAuditLoadedAction({
                postedFlightId: action.postedFlightId,
                audits
              })
            ),
            catchError(err => of(actions.AirFrameLandingsAuditLoadErrorAction(err)))
          );
      })
    )
  );

  loadEngineCyclesAuditEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadEngineCyclesAuditAction),
      mergeMap(action =>
        this.postedFlightAuditService.getEngineCyclesAudit(action.postedFlightEngineId).pipe(
          map(audits =>
            actions.EngineCyclesAuditLoadedAction({
              postedFlightEngineId: action.postedFlightEngineId,
              audits
            })
          ),
          catchError(err => of(actions.EngineCyclesAuditLoadErrorAction(err)))
        )
      )
    )
  );

  loadNextPageEngineCyclesAuditEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageEngineCyclesAuditAction),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.postedFlight.engineCyclesAudit.lastAuditLogId;
        }
        return this.postedFlightAuditService
          .getEngineCyclesAudit(
            action.postedFlightEngineId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.EngineCyclesAuditLoadedAction({
                postedFlightEngineId: action.postedFlightEngineId,
                audits
              })
            ),
            catchError(err => of(actions.EngineCyclesAuditLoadErrorAction(err)))
          );
      })
    )
  );
  //VORCheck Data audit
  loadVORCheckDataAuditEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadVORCheckDataAuditAction),
      mergeMap(action =>
        this.postedFlightAuditService.getVORCheckDataAudit(action.aircraftProfileId).pipe(
          map(audits =>
            actions.VORCheckDataAuditLoadedAction({
              aircraftProfileId: action.aircraftProfileId,
              audits
            })
          ),
          catchError(err => of(actions.VORCheckDataAuditLoadErrorAction(err)))
        )
      )
    )
  );

  loadNextPageVORCheckDataAuditEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageVORCheckDataAuditAction),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.postedFlight.vorcheckDataAudit.lastAuditLogId;
        }
        return this.postedFlightAuditService
          .getVORCheckDataAudit(action.aircraftProfileId, nextPageNumber, undefined, lastAuditLogId)
          .pipe(
            map(audits =>
              actions.VORCheckDataAuditLoadedAction({
                aircraftProfileId: action.aircraftProfileId,
                audits
              })
            ),
            catchError(err => of(actions.VORCheckDataAuditLoadErrorAction(err)))
          );
      })
    )
  );

  loadPreFlightChecklistUserESignatureAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadPreFlightChecklistUserESignaturesAudit),
      mergeMap(action =>
        this.postedFlightAuditService
          .getPreFlightChecklistUserESignaturesAudit(action.postedFlightId)
          .pipe(
            map(audits =>
              actions.PreFlightChecklistUserESignaturesAuditLoaded({
                postedFlightId: action.postedFlightId,
                audits
              })
            ),
            catchError(err => of(actions.PreFlightChecklistUserESignaturesAuditLoadError(err)))
          )
      )
    )
  );

  loadNextPagePreFlightChecklistUserESignaturesAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPagePreFlightChecklistUserESignaturesAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId =
            state.sharedAudit.postedFlight.preFlightChecklistUserESignaturesAudit.lastAuditLogId ===
            Infinity
              ? null
              : state.sharedAudit.postedFlight.preFlightChecklistUserESignaturesAudit
                  .lastAuditLogId;
        }
        return this.postedFlightAuditService
          .getPreFlightChecklistUserESignaturesAudit(
            action.postedFlightId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.PreFlightChecklistUserESignaturesAuditLoaded({
                postedFlightId: action.postedFlightId,
                audits
              })
            ),
            catchError(err => of(actions.PreFlightChecklistUserESignaturesAuditLoadError(err)))
          );
      })
    )
  );

  loadPostedFlightChecklistUserESignatureAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadPostedFlightChecklistUserESignaturesAudit),
      mergeMap(action =>
        this.postedFlightAuditService
          .getPostedFlightChecklistUserESignaturesAudit(action.postedFlightId)
          .pipe(
            map(audits =>
              actions.PostedFlightChecklistUserESignaturesAuditLoaded({
                postedFlightId: action.postedFlightId,
                audits
              })
            ),
            catchError(err => of(actions.PostedFlightChecklistUserESignaturesAuditLoadError(err)))
          )
      )
    )
  );

  loadNextPagePostedFlightChecklistUserESignaturesAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPagePostedFlightChecklistUserESignaturesAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId =
            state.sharedAudit.postedFlight.postedFlightChecklistUserESignaturesAudit
              .lastAuditLogId === Infinity
              ? null
              : state.sharedAudit.postedFlight.postedFlightChecklistUserESignaturesAudit
                  .lastAuditLogId;
        }
        return this.postedFlightAuditService
          .getPostedFlightChecklistUserESignaturesAudit(
            action.postedFlightId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.PostedFlightChecklistUserESignaturesAuditLoaded({
                postedFlightId: action.postedFlightId,
                audits
              })
            ),
            catchError(err => of(actions.PostedFlightChecklistUserESignaturesAuditLoadError(err)))
          );
      })
    )
  );

  loadFlightLogUserESignatureAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadFlightLogUserESignaturesAudit),
      mergeMap(action =>
        this.postedFlightAuditService.getFlightLogUserESignaturesAudit(action.postedFlightId).pipe(
          map(audits =>
            actions.FlightLogUserESignaturesAuditLoaded({
              postedFlightId: action.postedFlightId,
              audits
            })
          ),
          catchError(err => of(actions.FlightLogUserESignaturesAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageFlightLogUserESignaturesAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageFlightLogUserESignaturesAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId =
            state.sharedAudit.postedFlight.flightLogUserESignaturesAudit.lastAuditLogId === Infinity
              ? null
              : state.sharedAudit.postedFlight.flightLogUserESignaturesAudit.lastAuditLogId;
        }
        return this.postedFlightAuditService
          .getFlightLogUserESignaturesAudit(
            action.postedFlightId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.FlightLogUserESignaturesAuditLoaded({
                postedFlightId: action.postedFlightId,
                audits
              })
            ),
            catchError(err => of(actions.FlightLogUserESignaturesAuditLoadError(err)))
          );
      })
    )
  );

  loadSquawkUserESignatureAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadSquawkUserESignaturesAudit),
      mergeMap(action =>
        this.postedFlightAuditService.getSquawkUserESignaturesAudit(action.postedFlightId).pipe(
          map(audits =>
            actions.SquawkUserESignaturesAuditLoaded({
              postedFlightId: action.postedFlightId,
              audits
            })
          ),
          catchError(err => of(actions.SquawkUserESignaturesAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageSquawkUserESignaturesAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageSquawkUserESignaturesAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId =
            state.sharedAudit.postedFlight.squawkUserESignaturesAudit.lastAuditLogId === Infinity
              ? null
              : state.sharedAudit.postedFlight.squawkUserESignaturesAudit.lastAuditLogId;
        }
        return this.postedFlightAuditService
          .getSquawkUserESignaturesAudit(
            action.postedFlightId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.SquawkUserESignaturesAuditLoaded({
                postedFlightId: action.postedFlightId,
                audits
              })
            ),
            catchError(err => of(actions.SquawkUserESignaturesAuditLoadError(err)))
          );
      })
    )
  );
}
